import {
    Component,
    OnInit,
    Input,
    ViewChild,
    ElementRef,
    Output,
    EventEmitter,
    HostListener,
} from '@angular/core';

import { ApplicationService } from '../../../services/app.service';
import { BaseComponent } from '../../../shared/base.component';
import { User } from '../../../services/data/users/user.class';
import { HttpClient } from '@angular/common/http';
import { PexipService, IPexipCallOptions, IPexipEvent } from 'src/app/services/pexip.service';
import { Space } from 'src/app/services/data/spaces/space.class';

@Component({
    selector: 'pexip-window',
    templateUrl: './pexip.component.html',
    styleUrls: ['./pexip.component.scss'],
})
export class PexipWindowComponent extends BaseComponent implements OnInit {
    /** Active User */
    @Input() public user: User;
    /** Active Room */
    @Input() public space: Space;

    /** Whether the services are loading */
    public loading: string;
    /** Number of attempts to check for active meeting */
    public tries = 0;
    /** Whether to show self view */
    public show_self_view = true;

    /**
     *  Pexip-specific control variables
     */
    public pexip_options: IPexipCallOptions = {
        node: '',
        conference_id: null,
        name: null,
    };

    /** toggles for mic/camera */
    public admin_mute = false;
    public isMuted = false;
    public isCameraOff = false;
    public is_presenting = false;

    /** State variables for presentation display */
    public show_presentation = false;
    public presenter_name: string;
    public stream_loading = false;

    /** Pass through status of pexip call */
    @Output() public callStatusUpdate = new EventEmitter<IPexipEvent>();

    /** Emit the current connections in pexip (List of Attendees in Pexip is the "roster") */
    @Output() public attendeeUpdate = new EventEmitter<string>();

    /** Video element of user webcam (SelfView)  */
    @ViewChild('camera', { static: false }) cameraSelfView: ElementRef;

    /** Video element of main conference screen (Main Screen)  */
    @ViewChild('mainscreen', { static: false }) mainScreen: ElementRef;

    /** Video element of main presentation screen (Present Screen)  */
    @ViewChild('presentationscreen', { static: false }) presentScreen: ElementRef;

    /** Video element of the self presentation screen (Present Self Screen)  */
    @ViewChild('presentself', { static: false }) presentSelfScreen: ElementRef;

    /** Input that allows external connection driven from parent object */
    @Input() public connected = true;

    /** Unloads Pexip connection upon refresh/close of window/tab */
    @HostListener('window:beforeunload', ['$event'])
    unloadHandler(event: Event) {
        this.disconnectCamera();
        this.pexip.clear();
    }

    constructor(
        private service: ApplicationService,
        private pexip: PexipService,
        private http: HttpClient
    ) {
        super();
    }

    public ngOnInit(): void {
        this.loading = 'Application is initialising...';
        this.isCameraOff = false;
        this.isMuted = false;

        if (!this.service.is_ready) {
            return this.timeout('init', () => this.ngOnInit());
        }
        let room: any = {};
        this.subscription(
            'meeting',
            this.service.listen<User>('meeting', (meeting) => {
                if (meeting) {
                    this.pexip.clear();
                    this.pexip.init();
                    this.disconnectCamera();
                    this.pexip_options.pin = meeting.pexip_pin;
                    this.pexip_options.conference_id = meeting.pexip_id;
                    this.pexip_options.node = this.space.pexip_url.replace(/^https?:\/\//, '');
                    room = meeting;
                    this.loading = null;
                    this.tries = 0;
                    this.pollConnection().then(
                        () => {
                            this.loading = null;
                            if (this.pexip_options.conference_id) {
                                this.pexip_options.name = this.user.display_name;
                                this.initialise(this.pexip_options);
                            } else {
                                this.service.notifyError('Invalid meeting identifier');
                            }
                        },
                        () => {
                            this.service.notifyError('Error creating meeting');
                            this.service.navigate(['/', room.short_name]);
                        }
                    );
                }
            })
        );
    }

    /**
     * INIT Pexip and make call.
     *
     * All of these variables are set above with conference and userpin being set by Engine
     * @param options Pexip Call options
     */

    private initialise(options: IPexipCallOptions) {
        this.subscription(
            'pexip',
            this.pexip.events.subscribe((event) => {
                switch (event.message_type) {
                    case 'setup':
                        this.doneSetup(event.stream, event.pin_status);
                        break;
                    case 'connect':
                        this.onConnect(event.stream);
                        break;
                    case 'error':
                    case 'disconnect':
                        this.disconnectCamera();
                        this.remoteDisconnect(event.error || event.reason);
                        break;
                    case 'refresh':
                        this.onRefresh();
                        break;
                    case 'screenshare':
                        this.present(event.stream);
                        break;
                    case 'screenshare_stop':
                        this.presentCleanup();
                        break;
                    case 'participant_update':
                        this.admin_mute = event.participant.is_muted === 'YES';
                        break;
                    case 'presentation_preparing':
                        this.presentationPrepare(event.presenter);
                        break;
                    case 'presentation_connect':
                        this.presentation(event.stream);
                        break;
                    case 'presentation_disconnect':
                        this.presentationdisconnect();
                        break;
                    case 'end':
                        this.disconnectCamera();
                        this.callStatusUpdate.emit({ type: 'end' });
                        break;
                }
            })
        );
        this.pexip.call(options);
    }

    /**
     * Poll pexip until meeting has been setup
     */
    public pollConnection(): Promise<void> {
        this.loading = 'Waiting for your meeting to be ready...';
        return new Promise((resolve, reject) => {
            const url = `https://${this.pexip_options.node}/api/client/v2/conferences/${this.pexip_options.conference_id}/request_token`;
            this.http.post(url, null).subscribe(
                (data) => {
                    console.log('Data:', data);
                },
                (err) => {
                    console.log('Error:', err);
                    if (this.tries > 30) {
                        return reject();
                    }
                    if (err.error.status === 'success') {
                        resolve();
                    } else {
                        this.tries++;
                        this.timeout(
                            'poll_pexip',
                            () =>
                                this.pollConnection().then(
                                    () => resolve(),
                                    () => reject()
                                ),
                            20 * 1000
                        );
                    }
                },
                () => null
            );
        });
    }

    /**
     * Hard destruction of camera object.
     */
    public disconnectCamera() {
        if (
            this.cameraSelfView &&
            this.cameraSelfView.nativeElement &&
            this.cameraSelfView.nativeElement.srcObject
        ) {
            this.cameraSelfView.nativeElement.srcObject
                .getTracks()
                .forEach((track) => track.stop());
        }
        if (this.mainScreen && this.mainScreen.nativeElement) {
            this.mainScreen.nativeElement.src = '';
        }
    }

    /**
     * Pexip Toggle Camera for user
     *
     * this.rtc.muteVideo() returns the newly toggled state on the PexRTC object.
     * i.e. if user is currently not streaming their camera, running muteVideo() will result in the camera being streamed and returning: false;
     */
    public toggleCamera() {
        this.pexip.setVideoMute(!this.pexip.muted_video);
        this.isCameraOff = this.pexip.muted_video;
    }

    /**
     * Pexip Toggle for screenshare
     */
    public togglePresent() {
        if (this.is_presenting) {
            this.pexip.endPresentScreen();
        } else {
            this.pexip.presentScreen();
        }
    }

    /**
     * Function to begin display of local screenshare display
     */
    public present(videoURL: any) {
        this.is_presenting = true;
        if (typeof MediaStream !== 'undefined' && videoURL instanceof MediaStream) {
            this.presentSelfScreen.nativeElement.srcObject = videoURL;
        } else {
            this.presentSelfScreen.nativeElement.src = videoURL;
        }
        this.presentSelfScreen.nativeElement.muted = true;
    }

    /**
     * Clean up after presenting local client's screen
     */
    public presentCleanup() {
        this.is_presenting = false;
        this.presenter_name = null;
    }

    /**
     * Prepare to recieve an incoming presentation/screenshare
     */
    public presentationPrepare(presenter: string) {
        this.show_presentation = true;
        this.stream_loading = true;
        this.presenter_name = presenter.replace(/  *\<[^)]*\)\> */g, '');
    }

    /**
     * Presentation URL has been retrieved and is now buffering.
     */
    public presentation(videoURL: any) {
        this.timeout(
            'close_loader',
            () => {
                this.stream_loading = false;
            },
            1000
        );
        this.presentScreen.nativeElement.poster = '';
        if (typeof MediaStream !== 'undefined' && videoURL instanceof MediaStream) {
            this.presentScreen.nativeElement.srcObject = videoURL;
        } else {
            this.presentScreen.nativeElement.src = videoURL;
        }
    }

    /**
     * Presentation has stopped.
     */
    public presentationdisconnect() {
        this.show_presentation = false;
    }

    /**
     * Pexip Toggle Mute for user
     *
     * this.rtc.muteAudio() returns the newly toggled state on the PexRTC object.
     * i.e. if user is currently muted, running muteAudio() will result in the user being unmuted and returning: false;
     */
    public toggleMute() {
        if (this.pexip.host_muted_audio) return;
        this.pexip.setAudioMute(!this.pexip.muted_audio);
        this.isMuted = this.pexip.muted_audio;
    }

    /** Toggle Self View */
    public toggleSelfView(): void {
        this.show_self_view = !this.show_self_view;
    }

    /* ~~~ SETUP AND TEARDOWN ~~~ */

    /**
     * Manual end call
     */
    public endCall() {
        this.pexip.endCall();
    }

    /**
     * Handler for Pexip Remote Disconnection
     * @param reason error message returned
     */
    private remoteDisconnect(reason) {
        this.service.notifyError(reason || 'Disconnected');
        this.callStatusUpdate.emit({ type: 'disconnected', message: reason });
    }

    /**
     * Handler for Pexip completing setup
     * @param videoURL mediastream (or url) returned by Pexip of user webcam
     * @param pin_status current status of pin requirement : "none" | "required" | "optional"
     */
    private doneSetup(videoURL, pin_status) {
        if (typeof MediaStream !== 'undefined' && videoURL instanceof MediaStream) {
            this.cameraSelfView.nativeElement.srcObject = videoURL;
        } else {
            this.cameraSelfView.nativeElement.src = videoURL;
        }
        this.cameraSelfView.nativeElement.muted = true;
        this.loading = '';
    }

    /**
     * Handler for Pexip on connect
     * @param videoURL mediastream (or url) returned by Pexip for the main screen video object
     */
    private onConnect(videoURL) {
        this.mainScreen.nativeElement.poster = '';
        if (typeof MediaStream !== 'undefined' && videoURL instanceof MediaStream) {
            this.mainScreen.nativeElement.srcObject = videoURL;
        } else {
            this.mainScreen.nativeElement.src = videoURL;
        }
    }

    /**
     * Handler for Pexip hold/resume - CURRENTLY NOT USED
     * @param setting current setting of hold/resume state
     */
    private holdResume(setting) {}

    /** Handler for refresh */
    public onRefresh() {
        this.pexip.clear();
        this.ngOnInit();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
        this.disconnectCamera();
        this.pexip.clear();
    }
}
